import { useEffect, useState } from "react";
import { Loader2, Building2, Users, CircleDollarSign, Activity, BadgeCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const TINT = {
  active: "text-emerald-600 dark:text-emerald-300",
  trial: "text-sky-600 dark:text-ink-gold",
  suspended: "text-amber-600 dark:text-amber-300",
  deactivated: "text-rose-600 dark:text-rose-300",
};

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/platform/dashboard").then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); else toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 dark:text-ink-gold" /></div>;
  if (denied) return <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500 dark:border-ink-line" data-testid="platform-denied">Platform administration requires a platform-scoped account.</div>;
  if (!data) return null;

  const cards = [
    { label: "Customer Organizations", value: data.organizations.total, icon: Building2 },
    { label: "Active Subscriptions", value: data.organizations.active, icon: BadgeCheck },
    { label: "Active Trials", value: data.organizations.trial, icon: Activity },
    { label: "Platform Users", value: data.platform_users, icon: Users },
  ];
  const money = (v) => `${data.metrics.currency} ${Number(v).toLocaleString()}`;

  return (
    <div className="space-y-8" data-testid="platform-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-indigo-400 dark:text-ink-gold">PETRINOS Travel OS</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text sm:text-3xl">Platform Operations</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">HelStèEn platform overview — subscriptions and revenue only. No customer travel data is shown here.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} data-testid={`platform-kpi-${c.label}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{c.label}</span>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-ink-gold/10 dark:text-ink-gold"><Icon className="h-[18px] w-[18px]" /></div>
              </div>
              <div className="mt-3 font-mono-tf text-3xl font-bold text-slate-900 dark:text-ink-text">{c.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="flex items-center gap-2 text-slate-500"><CircleDollarSign className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Monthly Recurring Revenue</span></div>
          <div className="mt-2 font-mono-tf text-3xl font-bold text-slate-900 dark:text-ink-text" data-testid="platform-mrr">{money(data.metrics.mrr)}</div>
          <div className="mt-1 text-[11px] text-slate-400">{data.metrics.note}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="flex items-center gap-2 text-slate-500"><Activity className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Annual Recurring Revenue</span></div>
          <div className="mt-2 font-mono-tf text-3xl font-bold text-slate-900 dark:text-ink-text" data-testid="platform-arr">{money(data.metrics.arr)}</div>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold text-slate-900 dark:text-ink-text">Subscription Status</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {["active", "trial", "suspended", "deactivated"].map((k) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid={`org-status-${k}`}>
              <div className={`font-mono-tf text-2xl font-bold ${TINT[k]}`}>{data.organizations[k]}</div>
              <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{k}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold text-slate-900 dark:text-ink-text">Plan Distribution</h2>
        {data.plan_distribution.length === 0 ? (
          <p className="text-sm text-slate-500">No subscriptions linked yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.plan_distribution.map((p) => (
              <div key={p.plan_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid={`plan-dist-${p.plan_id}`}>
                <div className="text-sm font-semibold text-slate-800 dark:text-ink-text">{p.name}</div>
                <div className="font-mono-tf text-2xl font-bold text-indigo-600 dark:text-ink-gold">{p.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
